import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend } from 'recharts';
import { directeurCompteCategories } from '../data/directeurCompteReferentiel';

interface DirecteurCompteSpiderChartProps {
  className?: string;
}

// Données simulées pour un Directeur de Compte exemple
const directeurCompteData = {
  name: "Directeur de Compte Exemple",
  asis: {
    "Stratégie & Planification Commerciale": 84,
    "Gestion Client & Relation": 81,
    "Performance & Objectifs": 86,
    "Négociation & Closing": 82,
    "Communication & Présentation": 84,
    "Compréhension Technique TI": 76,
    "Prospection & Qualification": 80,
    "CRM & Pipeline Management": 79
  },
  target: {
    "Stratégie & Planification Commerciale": 92,
    "Gestion Client & Relation": 88,
    "Performance & Objectifs": 95,
    "Négociation & Closing": 90,
    "Communication & Présentation": 90,
    "Compréhension Technique TI": 85,
    "Prospection & Qualification": 88,
    "CRM & Pipeline Management": 87
  }
};

export default function DirecteurCompteSpiderChart({ className = "" }: DirecteurCompteSpiderChartProps) {
  // Préparer les données pour le graphique
  const chartData = directeurCompteCategories.map(category => ({
    category: category.name,
    ASIS: directeurCompteData.asis[category.name as keyof typeof directeurCompteData.asis],
    Target: directeurCompteData.target[category.name as keyof typeof directeurCompteData.target],
    Minimum: category.minScore
  }));

  // Calculer les scores moyens
  const asisAverage = Math.round(
    Object.values(directeurCompteData.asis).reduce((sum, score) => sum + score, 0) / Object.values(directeurCompteData.asis).length
  );
  const targetAverage = Math.round(
    Object.values(directeurCompteData.target).reduce((sum, score) => sum + score, 0) / Object.values(directeurCompteData.target).length
  );

  return (
    <div className={`bg-white rounded-lg p-6 shadow-md ${className}`}>
      <div className="mb-6">
        <h3 className="text-xl font-bold text-bz-dark mb-2">
          📊 Profil Directeur de Compte Exemple
        </h3>
        <p className="text-gray-600 text-sm mb-4">
          Visualisation ASIS vs Target - État actuel vs objectifs d'excellence commerciale
        </p>
        
        {/* Scores moyens */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
            <div className="text-sm text-orange-600 font-medium">Score ASIS (Actuel)</div>
            <div className="text-2xl font-bold text-orange-700">{asisAverage}%</div>
            <div className="text-xs text-orange-500">État actuel</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="text-sm text-green-600 font-medium">Score Target (Objectif)</div>
            <div className="text-2xl font-bold text-green-700">{targetAverage}%</div>
            <div className="text-xs text-green-500">Excellence visée</div>
          </div>
        </div>
      </div>

      {/* Graphique Spider */}
      <div className="h-96 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={chartData} margin={{ top: 20, right: 80, bottom: 20, left: 80 }}>
            <PolarGrid stroke="#e5e7eb" />
            <PolarAngleAxis 
              dataKey="category" 
              tick={{ fontSize: 12, fill: '#374151' }}
              className="text-xs"
            />
            <PolarRadiusAxis 
              angle={90} 
              domain={[0, 100]} 
              tick={{ fontSize: 10, fill: '#6b7280' }}
              tickCount={6}
            />
            
            {/* Ligne ASIS (État actuel) */}
            <Radar
              name="ASIS (Actuel)"
              dataKey="ASIS"
              stroke="#FF6B35"
              fill="#FF6B35"
              fillOpacity={0.1}
              strokeWidth={3}
              dot={{ fill: '#FF6B35', strokeWidth: 2, r: 4 }}
            />
            
            {/* Ligne Target (Objectif) */}
            <Radar
              name="Target (Objectif)"
              dataKey="Target"
              stroke="#00B894"
              fill="#00B894"
              fillOpacity={0.05}
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ fill: '#00B894', strokeWidth: 2, r: 3 }}
            />
            
            {/* Ligne Minimum requis */}
            <Radar
              name="Minimum (75%)"
              dataKey="Minimum"
              stroke="#E74C3C"
              fill="transparent"
              strokeWidth={1}
              strokeDasharray="2 2"
              dot={false}
            />
            
            <Legend 
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="circle"
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Analyse des écarts */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-3">🎯 Points Forts (ASIS ≥ 85%)</h4>
          <div className="space-y-1">
            {chartData
              .filter(item => item.ASIS >= 85)
              .map(item => (
                <div key={item.category} className="flex justify-between text-sm">
                  <span className="text-gray-700">{item.category}</span>
                  <span className="font-bold text-green-600">{item.ASIS}%</span>
                </div>
              ))}
          </div>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-3">📈 Axes d&apos;Amélioration (ASIS &lt; 80%)</h4>
          <div className="space-y-1">
            {chartData
              .filter(item => item.ASIS < 80)
              .map(item => (
                <div key={item.category} className="flex justify-between text-sm">
                  <span className="text-gray-700">{item.category}</span>
                  <span className="font-bold text-orange-600">{item.ASIS}%</span>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Légende */}
      <div className="mt-4 text-xs text-gray-500 text-center">
        <p>
          <span className="inline-block w-3 h-3 bg-orange-500 rounded-full mr-1"></span>
          <strong>ASIS</strong> = État actuel • 
          <span className="inline-block w-3 h-3 bg-green-500 rounded-full mx-1"></span>
          <strong>Target</strong> = Objectif d'excellence • 
          <span className="inline-block w-3 h-3 bg-red-500 rounded-full mx-1"></span>
          <strong>Minimum</strong> = Seuil requis (75%)
        </p>
      </div>
    </div>
  );
}

